import es from '../i18n/es';
import { squadKit } from './teamFixtures';

// ── Fixture Finder text ──────────────────────────────────────────────────────
// The "Copy Fixture Finder" button on a squad builds a short WhatsApp-style
// message that MST posts to Spanish clubs to find an opponent, so it is
// written in Spanish and gives the age as the Spanish football category
// (Infantil, Cadete, Juvenil…) rather than a UK "U15".

// Spanish categories are by age reached during the season's first calendar
// year (seasons run roughly September to June).
const CATEGORIES = [
  { maxAge: 7,  name: 'Prebenjamín' },
  { maxAge: 9,  name: 'Benjamín' },
  { maxAge: 11, name: 'Alevín' },
  { maxAge: 13, name: 'Infantil' },
  { maxAge: 15, name: 'Cadete' },
  { maxAge: 18, name: 'Juvenil' },
];

const GENDER_ES = { Male: 'Masculino', Female: 'Femenino', Mixed: 'Mixto' };
const ABILITY_ES = { Low: 'Bajo', Medium: 'Medio', High: 'Alto' };

/** The year a Spanish season starts in, for a date (July onwards = new season). */
export function seasonStartYear(date) {
  return date.getMonth() >= 6 ? date.getFullYear() : date.getFullYear() - 1;
}

/** Spanish category for a birth year, for a fixture on the given date. */
export function spanishCategory(birthYear, date = new Date()) {
  const age = seasonStartYear(date) - Number(birthYear);
  if (!Number.isFinite(age)) return null;
  return (CATEGORIES.find((c) => age <= c.maxAge) || { name: 'Senior' }).name;
}

function joinEs(items) {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

const capitalise = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v);
const colourEs = (c) => capitalise(es.values.colour[String(c).trim().toLowerCase()] || String(c).trim());

/** "Rojo y blanco (rayas verticales)" — a kit's shirt in Spanish. */
function shirtEs(part) {
  const patterned = part.pattern && part.pattern !== 'plain';
  const colours = part.jersey2 && patterned
    ? `${colourEs(part.jersey)} y ${colourEs(part.jersey2).toLowerCase()}`
    : colourEs(part.jersey);
  return patterned ? `${colours} (${es.values.pattern[part.pattern] || part.pattern})` : colours;
}

/** "Camiseta: Rojo · Pantalón: Negro · Medias: Rojo" for one kit, or null. */
function kitEs(part) {
  if (!part) return null;
  const bits = [
    part.jersey && `Camiseta: ${shirtEs(part)}`,
    part.shorts && `Pantalón: ${colourEs(part.shorts)}`,
    part.socks && `Medias: ${colourEs(part.socks)}`,
  ].filter(Boolean);
  return bits.length ? bits.join(' · ') : null;
}

/**
 * The team's name for the message: the group's name, plus the squad's own
 * label when staff gave it one (not the default "Squad 2").
 */
function teamNameEs(sq, request) {
  const group = (request.internalRef || request.tourName || '').trim();
  const label = (sq.label || '').trim();
  const customLabel = label && !/^Squad \d+$/.test(label) ? label : '';
  if (group) return customLabel && customLabel.toLowerCase() !== group.toLowerCase() ? `${group} (${customLabel})` : group;
  if (customLabel) return customLabel;
  const fallback = label || (request.contactName || '').trim();
  return fallback ? fallback.replace(/^Squad (\d+)$/, 'Equipo $1') : null;
}

function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The Spanish fixture finder message for one squad.
 * `request` supplies the tour dates and the fallback team name.
 */
export function buildFixtureFinderText(sq, request = {}, today = new Date()) {
  const pf = sq.preferredFixture || {};
  const yobs = (Array.isArray(sq.yearsOfBirth) ? sq.yearsOfBirth : (sq.yearOfBirth ? [sq.yearOfBirth] : []))
    .map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  const fixtureDate = parseDate(pf.preferredDate) || parseDate(request.departureDate) || today;

  let category = null;
  if (yobs.length) {
    // Oldest category first, e.g. "Juvenil / Cadete" for a 2010–2011 squad.
    const cats = [...new Set(yobs.map((y) => spanishCategory(y, fixtureDate)))];
    const gender = GENDER_ES[sq.gender] ? ` ${GENDER_ES[sq.gender]}` : '';
    category = `${cats.join(' / ')}${gender} (nacidos en ${joinEs(yobs.map(String))})`;
  }

  const numFixtures = (sq.fixtures || []).length || 1;
  const name = teamNameEs(sq, request);
  const date = parseDate(pf.preferredDate);
  const kit = squadKit(sq);
  const homeKit = kitEs(kit?.home);
  const awayKit = kitEs(kit?.away);

  return [
    `⚽ ${numFixtures} ${numFixtures === 1 ? 'PARTIDO DISPONIBLE' : 'PARTIDOS DISPONIBLES'} ⚽`,
    date ? `📅 Fecha - ${date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}` : null,
    pf.preferredTime ? `⏰ Hora - ${pf.preferredTime}` : null,
    pf.preferredLocation ? `🏟️ Campo - ${pf.preferredLocation}` : null,
    category ? `👦 Categoría - ${category}` : null,
    sq.playersPerMatch ? `⚽ Fútbol ${sq.playersPerMatch}` : null,
    sq.abilityLevel ? `📈 Nivel - ${ABILITY_ES[sq.abilityLevel] || sq.abilityLevel}` : null,
    name ? `✏️ Equipo - ${name}` : null,
    homeKit ? `👕 ${awayKit ? 'Equipación local' : 'Equipación'} - ${homeKit}` : null,
    awayKit ? `👕 ${homeKit ? 'Equipación visitante' : 'Equipación'} - ${awayKit}` : null,
  ].filter(Boolean).join('\n');
}
